import React from 'react'
import Link from 'gatsby-link'
import { Button, Card, CardText, Grid, Cell } from 'react-md'
import EntitySplash from '../components/EntitySplash'
import Lottie from 'react-lottie'

import * as animationData_DoubleTaxation from '../animations/double_taxation/data.json'
import * as animationData_PassThroughTaxation from '../animations/pass_through_taxation/data.json'

import FootnoteIcon from '../components/FootnoteIcon'
import FootnotesList from '../components/FootnotesList'
import TableOfContents from '../components/TableOfContents'

import MaterialTable from '../components/MaterialTable'
import TermsModal from '../components/TermsModal'

import TopDisclaimer from '../components/TopDisclaimer';

const EntityFormationPage = () => {

  const footnotes = [
    "Examples of this include: failure to hold Board meetings, failure to abide by the procedures set forth in the Bylaws, commingling personal and company funds (e.g., putting the company's money in a personal bank account), etc.",
    "Investors generally wouldn't make an investment directly to an individual, i.e., in the case of a sole proprietor.",
    "Certain types of entities provide more liquidity than others. An entity that makes major decisions by MAJORITY RULE (e.g., a Corporation) is more likely to provide liquidity. In contrast, an entity that requires UNANIMITY on major decisions (e.g., a Partnership or LLC) is less likely to provide liquidity."
  ]

  return (
    <div className="content">
      <EntitySplash/>
      <Card className="md-block-centered main-content-card">
        <CardText>
          <Grid>
            <Cell size={8} style={{margin: 'auto'}}>
              <TopDisclaimer/>
              <TableOfContents
                contents={ [
                  'Who Cares?', 
                  'Risky Business',
                  'Not Making a Choice',
                  'Do You Need to Attract Outside Capital?',
                  'Do You Need Limited Liability?',
                  'Who is Involved in Making Decisions?',
                  'How Do You Want the Business to be Taxed?',
                  'For-Profit Entity Formation Overview Video',
                  'Nonprofit Entity Formation Overview Video',
                  'For-Profit and Nonprofit Collaboration',
                  'Step by Step Formation Resources',
                  'Footnotes'
                ] }
              />
              <h1>Choosing the right legal structure for your venture</h1>
              <h2 id="who-cares?" className="anchor-ref">Who Cares?</h2>
              <p>
                If your business does not potentially put others at risk and you do not need outside capital to invest in your business, then this module probably isn't for you. However, if there is a possibility that someone could get hurt, or that you'll need to attract external investors at some point in the future, keep reading on.
              </p>
              <h2 id="risky-business" className="anchor-ref">Risky Business</h2>
              <p>
                The level of risk involved in a business is a very fact-specific question, but here is a list of risky activities that indicate that you should probably file for a limited liability entity, such as an LLC or corporation:
              </p>
              <ul>
                <li>Food preparation</li>
                <li>Use of machinery</li>
                <li>Use of chemicals</li>
                <li>Use of vehicles</li>
                <li>Moving of heavy objects</li>
                <li>Use of high voltage power</li>
                <li>Use of other dangerous/toxic materials</li>
                <li>Construction</li>
                <li>Activities involving children</li>
                <li>Health-related treatment</li>
                <li>Assessment of risk (e.g., building inspectors)</li>
                <li>Travel (e.g., common carriers)</li>
                <li>Use of employees (who may commit negligence, or may be at risk of injury on the job)</li>
                <li>Publishing/reporting involving sensitive matters (risk of defamation/false light)</li>
                <li>Advising clients about anything significant (e.g., financial, health, legal)</li>
              </ul>
              <h2 id="not-making-a-choice" className="anchor-ref">Not Making a Choice</h2>
              <p>
                In the context of entity choice, not making a choice is still a choice. If you don't form an LLC or corporation, the law will presume that your business is a:
              </p>
              <ul>
                <li>Sole Proprietorship (for solo owners or married couples); or a</li>
                <li>Partnership (for 2+ unmarried co-founders)</li>
              </ul>
              <p>
                Unfortunately, neither of these entities provide you with limited liability. Read on for more details on limited liability vs. personal liability.
              </p>
              <h2 id="do-you-need-limited-liability?" className="anchor-ref">Do You Need Limited Liability?</h2>
              <h3>Personal Liability</h3>
              <p>
                If a company is subject to personal liability, and the company does not have enough money to pay damages in a lawsuit, then the plaintiff can collect the remaining damage award from the personal assets of the owner(s). The liability can be attributed to a sole proprietor or it can be shared in a partnership.
              </p>
              <h3>Limited Liability</h3>
              <p>
                A limited liability entity limits the owners' financial exposure to the amount that the owners have invested in the company or partnership. This means that in the event that the company is sued, the personal assets of the owner(s) are protected from liability and only the assets of the company can be touched.
              </p>
              <p>However, note that even if a company is formed as a limited liability entity, there are still situations where the company owners can be sued and found personally liable:</p>
              <ul>
                <li>Personal negligence</li>
                <li>Personal guarantees</li>
                <li>Intentional harm</li>
                <li>Failure to adhere to entity formalities <FootnoteIcon footnotes={ footnotes } number={ 1 }/></li>
                <li>Undercapitalization</li>
                <li>Leadership violation of fiduciary duties</li>
                <li>Law violations</li>
              </ul>
              <h2 id="do-you-need-to-attract-outside-capital?" className="anchor-ref">Do You Need to Attract Outside Capital?</h2>
              <p>
                All entities work well with:
              </p>
              <ul>
                <li>Founders' contributions</li>
                <li>Funding from friends and family</li>
                <li>Debt</li>
              </ul>
              <p>
                However, some entities will be more attractive to investors than others.
              </p>
              <h3>Equity Funding Considerations</h3>
              <p>
                Investors generally want to see the following:
              </p>
              <ul>
                <li>A formal entity to hold their investment <FootnoteIcon footnotes={ footnotes } number={ 2 }/></li>
                <li>Ability to profit share</li>
                <li>Ability to share decision-making power</li>
                <li>Liquidity</li>
              </ul>
              <p>
                See the Capitalization section to learn more about the types of capitalization that works best for each entity form.
              </p>
              <h3>Nonprofit Organizations</h3>
              <p>
                Nonprofits cannot accept equity-based investments from investors&#8212;they don't have any equity they can sell, as the assets of the nonprofit are supposed to be held in trust for the public good. Instead, nonprofits must rely on other sources of support, such as:
              </p>
              <ul>
                <li>Donations</li>
                <li>Grants</li>
                <li>Government Support</li>
                <li>Debt</li>
                <li>Service Income</li>
              </ul>
              <h2>Who is Involved in Making Decisions?</h2>
              <p>
                Choice of entity can play a large role in the decision-making process (governance) within your business. Corporations have a rigid, hierarchical structure of governance involving a Board of Directors, corporate officers, and shareholders. On the other hand, LLCs have a more flexible governance arrangement that allows members to agree to very specific and custom terms about how decisions are reached.
              </p>
              <h3>Entity Comparison</h3>
              <MaterialTable
                headElements={ ['Name', 'Decision-making'] } 
                rowElements={ [ 
                  [ 'Sole Proprietorship', '	The Sole Proprietor (or a married couple) makes the decisions' ],
                  [ 'General Partnership', 'All Partners make the decisions' ],
                  [ 'Limited Partnership', 'The General Partner(s) make the decisions' ],
                  [ 'For-Profit C Corporation', 'The Board (elected by the shareholders) makes the decisions' ],
                  [ 'For-Profit S Corporation', 'The Board (elected by the shareholders) makes the decisions' ],
                  [ 'Nonprofit Corporation', 'The Board makes the decisions' ],
                  [ 'LLC (Member-Managed)', '	The Members make the decisions' ],
                  [ 'LLC (Manager-Managed)', 'The Members make the decisions, but give some power to Manager' ],
                  [ 'Cooperative Corporation', 'The Board (elected by the members) makes the decisions' ]
                ] } />
              <h2 id="how-do-you-want-the-business-to-be-taxed?" className="anchor-ref">How Do You Want the Business to be Taxed?</h2>
              <p>
                There are generally three different taxation structures that are determined by your type of business entity. The way that your business will be taxed is an important aspect to consider in deciding what type of entity to form.
              </p>
              <h3>No Taxation</h3>
              <p>
                The entity is exempt from income taxes.
              </p>
              <p>
                Eligible entities:
              </p>
              <ul>
                <li>This is reserved for nonprofit organizations that have tax exempt status from the IRS and the state.</li>
              </ul>
              <h3>Entity Level Taxation</h3>
              <p>
                Also referred to as "double taxation." The entity reports and pays taxes on net profits through the entity's tax returns. The owners of the entity then report and pay taxes through their personal tax returns on any profit sharing they receive (at a lower "dividend" tax rate).
              </p>
              <div style={{maxWidth: 800, margin: 'auto'}}>
                <Lottie options={ {loop: true, autoplay: true, animationData: animationData_DoubleTaxation} }/>
              </div>
              <p>
                As shown in the animation, corporate profits are taxed first at the entity level. Then, if the company decides to distribute profits to its owners, the owners will report that income in their own personal tax filings and pay taxes on an individual level.
              </p>
              <p>
                Eligible entities:
              </p>
              <ul>
                <li>Corporation</li>
                <li>LLC (after filing <a href="https://www.irs.gov/pub/irs-pdf/f8832.pdf" target="_blank" rel="noopener noreferrer">Form 8832</a>)</li>
              </ul>
              <h3>Pass-Through Taxation</h3>
              <p>
                If treated as a pass-through entity, the entity itself is not considered a taxpayer, but it still reports its net profits to the IRS. All net profits "pass through" to the owners as income, and the owners report and pay any taxes owed through their individual tax returns.
              </p>
              <div style={{maxWidth: 800, margin: 'auto'}}>
                <Lottie options={ {loop: true, autoplay: true, animationData: animationData_PassThroughTaxation} }/>
              </div>
              <p>
                Eligible entities:
              </p>
              <ul>
                <li>Sole Proprietorship</li>
                <li>General Partnership</li>
                <li>Limited Partnership</li>
                <li>LLC</li>
                <li>
                  Corporation (after filing <a href="https://www.irs.gov/pub/irs-pdf/f2553.pdf" target="_blank" rel="noopener noreferrer">Form 2553</a> to elect S Corporation tax treatment)
                </li>
              </ul>
              <h2 id="where-to-incorporate" className="anchor-ref">Where to Incorporate</h2>
              <p>
                If you've found that a corporation makes the most sense for your business goals based on the above four factors, then you'll also need to decide which state to incorporate in.
              </p>
              <h3>California vs. Delaware</h3>
              <p>
                There aren't any residency requirements for forming a corporation—every state is happy to accept your filing fees and franchise taxes—but if you're operating your company out of California, this decision probably comes down to either Delaware or California.
              </p>
              <h3>Delaware</h3>
              <p>
                If your company requires venture capital funding and you want to cast the widest possible net for investors, you'll likely want to form a Delaware corporation. This is due to a couple of reasons:
              </p>
              <ul>
                <li>
                  <b>Predictability: </b>Investors (and their attorneys) don't like uncertainty. If a dispute arises, Delaware corporations have access to the Delaware Court of Chancery, a specialized court made up of experienced business law judges, that exclusively hears business-related cases. While some other states also have a business court, the Court of Chancery is the oldest business court in the country, and as such, has a larger volume of prior opinions to draw from when analyzing disputes.
                </li>
                <li>
                  <b>Familiarity: </b>Delaware has enjoyed a significant first mover advantage by establishing effective and well-respected business law courts early on. Today, most of the publicly traded companies in the US are incorporated in Delaware. Because of this popularity, investors (and their attorneys) are very familiar with the corporate laws in Delaware. This further fuels investors' preference for Delaware, as a familiar body of law requires fewer additional research hours (read: legal fees) to clear a deal.
                </li>
              </ul>
              <p>
                Even if you incorporate in Delaware, however, if you're doing business in California, you probably need to register as a pseudo-foreign corporation in California, and pay California franchise tax in addition to the Delaware franchise tax.
              </p>
              <h3>California</h3>
              <p>
                While Delaware may have some advantages, incorporating in California by no means locks you out of raising investment. Many investors (and their lawyers) are familiar with California law due to the fact that California requires many companies operating within the state to abide by its Corporate Code, even if they are incorporated in another state. Additionally, you can always reincorporate after you attract more interest from investors. There are plenty of companies that started out as California corporations, and converted into Delaware corporations later on for their IPO.
              </p>
              <p>
                For an early stage startup in California that is still trying to discover and develop a viable business model, it might make more sense to forego the extra costs and filings of incorporating in Delaware and incorporate in California instead.
              </p>
              <h2 id="for-profit-entity-formation-overview-video" className="anchor-ref">For-Profit Entity Formation Overview Video</h2>
              <div style={ {position: 'relative', width: '100%', height: '0px', paddingBottom: '75%', marginBottom: '20px'} }>
                <iframe width="560" height="315" src="https://www.youtube-nocookie.com/embed/rX9TiJDMWgw" data-allow="autoplay; encrypted-media" style={ {position: 'absolute', left: '0px', top: '0px', width: '100%', height: '100%'} } allowFullScreen ></iframe>
              </div>
              <h2 id="nonprofit-entity-formation-overview-video" className="anchor-ref">Nonprofit Entity Formation Overview Video</h2>
              <div style={ {position: 'relative', width: '100%', height: '0px', paddingBottom: '75%', marginBottom: '20px'} }>
                <iframe width="560" height="315" src="https://www.youtube-nocookie.com/embed/OKrgu07W_4o" data-allow="autoplay; encrypted-media" style={ {position: 'absolute', left: '0px', top: '0px', width: '100%', height: '100%'} } allowFullScreen ></iframe>
              </div>
              <h2 id="for-profit-and-nonprofit-collaboration" className="anchor-ref">For-Profit and Nonprofit Collaboration</h2>
              <p>
                There are a number of ways that for-profit businesses and nonprofit organizations can collaborate together. In each variation, however, the nonprofit organization must maintain its independence of judgment and ensure that it continues to act primarily in furtherance of its tax-exempt purpose(s). Otherwise, the nonprofit risks jeopardizing its tax-exempt status. For more details, see our slide deck presentation in the <a href="/extra-permits-and-licenses#501(c)(3)-tax-exempt-status">Extra Permits and Licenses</a> module.
              </p>
              <h2>Resources for Further Information</h2>
              <ul>
                <li>Adler & Colvin: <a href="https://www.adlercolvin.com/pdf/social_enterprise/Operating_in_Two_Worlds_Tandem_Structures_in_Social_Enterprise_(403192).pdf" target="_blank" rel="noopener noreferrer">Operating in Two Worlds: Tandem Structures in Social Enterprise</a></li>
                <li>American Bar Association: <a href="https://www.americanbar.org/publications/blt/2014/06/03_levitt.html" target="_blank" rel="noopener noreferrer">Taking Care of Business: Use of a For-Profit Subsidiary by a Nonprofit Organization</a></li>
              </ul>
              <h2 id="step-by-step-formation-resources" className="anchor-ref">Step by Step Formation Resources</h2>
              <p>
                We don't want to reinvent the wheel here. There are many comprehensive write-ups on the mechanics of entity formation out there. Below are some of our favorites.
              </p>
              <h3>For-Profit Corporation</h3>
              <h4>FORMATION</h4>
              <ul>
                <li>
                  Nolo: <a href="http://www.nolo.com/legal-encyclopedia/how-form-corporation-california.html" target="_blank" rel="noopener noreferrer">How to Form a Corporation in California</a>
                </li>
                <li>
                  Nolo: <a href="http://www.nolo.com/legal-encyclopedia/how-form-corporation-delaware.html" target="_blank" rel="noopener noreferrer">How to Form a Corporation in Delaware</a>
                </li>
              </ul>
              <h4>TEMPLATE FORMS</h4>
              <ul>
                <li>
                  Orrick: <a href="https://www.orrick.com/Total-Access/Tool-Kit/Start-Up-Forms" target="_blank" rel="noopener noreferrer">Startup Forms Library</a>
                </li>
                <li>
                  Cooley: <a href="https://www.cooleygo.com/documents/" target="_blank" rel="noopener noreferrer">Cooley Go Documents Generator</a>
                </li>
                <li>
                  Goodwin Procter: <a href="http://www.foundersworkbench.com/document-driver/" target="_blank" rel="noopener noreferrer">Document Driver</a>
                </li>
                <li>
                  WilmerHale: <a href="https://launch.wilmerhale.com/build/document-generator/" target="_blank" rel="noopener noreferrer">WilmerHale Launch Document Generator</a>
                </li>
              </ul>
              <h3>Limited Liability Company</h3>
              <p>FORMATION</p>
              <ul>
                <li>
                  Nolo: <a href="http://www.nolo.com/legal-encyclopedia/california-form-llc-31670.html" target="_blank" rel="noopener noreferrer">How to Form a California LLC</a>
                </li>
              </ul>
              <h4>TEMPLATE FORMS</h4>
              <ul>
                <li>
                  Nolo (via SF Law Library): <a href="http://sflawlibrary.org/legal-databases#Nolo%20Legal%20Information%20Reference%20Center" target="_blank" rel="noopener noreferrer">Nolo Legal Information Center</a>
                </li>
                <li>
                  New Business Community Law Clinic: <TermsModal link="https://drive.google.com/open?id=0B3EPRVDJnG2Bd1hzMk93V0dwNUU" text="Member Managed LLC Operating Agreement"/>
                </li>
                <li>
                  New Business Community Law Clinic: <TermsModal link="https://drive.google.com/open?id=0B9zW8ghngOP2dm9jcldTaWhmVm8" text="Manager Managed LLC Operating Agreement"/>
                </li>
                <li>
                  New Business Community Law Clinic: <TermsModal link="https://drive.google.com/open?id=0B3EPRVDJnG2BX0k3MDBzYUhnTlk" text="LLC Owners' Manual"/>
                </li>
              </ul>
              <h3>Nonprofit Corporation</h3>
              <h4>FORMATION</h4>
              <ul>
                <li>
                  Nolo: <a href="http://www.nolo.com/legal-encyclopedia/forming-nonprofit-corporation-california-36053.html" target="_blank" rel="noopener noreferrer">How to Form a Nonprofit Corporation in California</a>
                </li>
                <li>
                  California Association of Nonprofits: <a href="http://www.calnonprofits.org/resources?id=65" target="_blank" rel="noopener noreferrer">How to Start a California Nonprofit</a>
                </li>
              </ul>
              <h4>TEMPLATE FORMS</h4>
              <ul>
                <li>
                  Public Counsel: <a href="http://www.publiccounsel.org/tools/publications/files/Annotated_Articles.pdf" target="_blank" rel="noopener noreferrer">Annotated Articles of Incorporation</a>
                </li>
                <li>
                  Public Counsel: <a href="http://www.publiccounsel.org/tools/publications/files/Annotated_Bylaws.pdf" target="_blank" rel="noopener noreferrer">Annotated Bylaws</a>
                </li>
                <li>
                  New Business Community Law Clinic: <TermsModal link="https://drive.google.com/open?id=1r8d6Lp0pOcxNgXurdRhV16AEQ1TWxM6C" text="Non-Profit Bylaws"></TermsModal>
                </li>
              </ul>
              <h2>Footnotes</h2>
              <FootnotesList footnotes={ footnotes }/>
            </Cell>
          </Grid>
        </CardText>
      </Card>
    </div>
  )
}

export default EntityFormationPage;


// WEBPACK FOOTER //
// ./src/pages/entity-formation.js